import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Leaf, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { GoogleAuthButton } from "../components/GoogleAuthButton";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.full_name || u.email}!`);
      const dest = u.role === "admin" ? "/admin" : (loc.state?.from?.pathname || "/dashboard");
      nav(dest, { replace: true });
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2" data-testid="login-page">
      <div className="hidden lg:flex flex-col justify-between bg-moss text-white p-12 relative overflow-hidden min-h-[600px]">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-terra/30 blur-3xl" />
        <div className="relative">
          <div className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-full bg-white/20 grid place-items-center"><Leaf size={18} /></span>
            <span className="font-heading text-xl font-semibold">Danashakaa.com</span>
          </div>
        </div>
        <div className="relative">
          <h2 className="font-heading text-3xl lg:text-4xl font-semibold leading-tight max-w-md">
            Welcome back. Your healthier rhythm is one login away.
          </h2>
          <p className="mt-4 text-white/80 max-w-md">
            Pick up where you left off — videos, PDFs, and your personal profile, all in one calm place.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <form onSubmit={handleSubmit} className="w-full max-w-md" data-testid="login-form">
          <h1 className="font-heading text-3xl font-semibold text-ink">Log in</h1>
          <p className="mt-2 text-muted2">New here? <Link to="/register" className="text-moss font-medium hover:underline">Create a free account</Link></p>

          <div className="mt-8">
            <GoogleAuthButton label="Continue with Google" testId="login-google-button" />
            <div className="my-6 flex items-center gap-3 text-xs text-soft">
              <span className="flex-1 h-px bg-subtle" />
              or use email
              <span className="flex-1 h-px bg-subtle" />
            </div>
          </div>

          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-ink mb-2">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="w-full rounded-xl border border-subtle bg-white px-4 py-3 text-ink focus:border-moss focus:outline-none focus:ring-2 focus:ring-moss/20 transition-all" />
            </div>
            <div>
              <label className="block text-sm font-medium text-ink mb-2">Password</label>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="w-full rounded-xl border border-subtle bg-white px-4 py-3 text-ink focus:border-moss focus:outline-none focus:ring-2 focus:ring-moss/20 transition-all" />
            </div>
            {error && <p className="text-sm text-danger" data-testid="login-error">{error}</p>}
            <button type="submit" disabled={busy} data-testid="login-submit-button"
              className="w-full inline-flex justify-center items-center gap-2 bg-terra hover:bg-terra-dark text-white rounded-full px-6 py-3 font-semibold transition-all active:scale-95 disabled:opacity-60">
              {busy && <Loader2 className="animate-spin" size={18} />} Log in
            </button>
            <div className="text-sm text-center">
              <Link to="/forgot-password" className="text-moss hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
